/**
 * PostHog client wrapper
 *
 * Idempotent init. Subscribes to consent and only initializes PostHog when
 * `consent.analytics === true`. Revokes (opt_out_capturing + cookie clear)
 * when consent is withdrawn. PII scrub runs on every capture.
 *
 * If `POSTHOG_KEY` is unset, ALL calls are NO-OPs — the app continues to
 * render with zero analytics overhead.
 */

import type { AnalyticsConfig, AnalyticsEventName, EventProps, IdentifyProps } from './types';
import { scrubProps } from './pii-scrub';

interface PostHogLike {
  init: (apiKey: string, options: Record<string, unknown>) => void;
  capture: (event: string, props?: Record<string, unknown>) => void;
  identify: (userId: string, props?: Record<string, unknown>) => void;
  opt_out_capturing: () => void;
  opt_in_capturing: () => void;
  reset: () => void;
  has_opted_out_capturing: () => boolean;
}

let posthogInstance: PostHogLike | null = null;
let isInitialized = false;
let currentConfig: AnalyticsConfig | null = null;

/**
 * Idempotent init. Safe to call multiple times — only the first call with
 * a valid apiKey actually loads the SDK.
 *
 * Returns true when init succeeded (or was already initialized). Returns
 * false when apiKey was missing — caller should treat captures as NO-OPs.
 */
export async function initPostHog(config: AnalyticsConfig): Promise<boolean> {
  if (isInitialized) return true;
  if (!config.apiKey) {
    currentConfig = config;
    return false;
  }
  if (typeof window === 'undefined') {
    // SSR — do not init on server. Use posthog-node for server captures.
    return false;
  }

  try {
    const mod = await import('posthog-js');
    const posthog = (mod.default ?? mod) as unknown as PostHogLike;
    posthog.init(config.apiKey, {
      api_host: config.host || 'https://us.i.posthog.com',
      person_profiles: 'identified_only',
      capture_pageview: false, // We capture page_viewed events manually
      capture_pageleave: true,
      disable_session_recording: true, // PII risk surface — off by default
      autocapture: false, // Explicit events only
      respect_dnt: true,
      opt_out_capturing_by_default: false,
      loaded: (ph: PostHogLike) => {
        if (config.debug) {
          // eslint-disable-next-line no-console
          console.log('[@unity/analytics] PostHog initialized', { app: config.appName });
        }
        // Re-attach instance after async load
        posthogInstance = ph;
      },
    });
    posthogInstance = posthog;
    currentConfig = config;
    isInitialized = true;
    return true;
  } catch (err) {
    if (config.debug) {
      // eslint-disable-next-line no-console
      console.warn('[@unity/analytics] PostHog init failed', err);
    }
    return false;
  }
}

/**
 * Capture an event. PII scrub runs first. NO-OP when PostHog not initialized
 * (apiKey missing or consent denied).
 */
export function capturePostHog(event: AnalyticsEventName, props: EventProps): void {
  if (!isInitialized || !posthogInstance) {
    if (currentConfig?.debug) {
      // eslint-disable-next-line no-console
      console.log('[@unity/analytics:capture (NO-OP)]', event, props);
    }
    return;
  }
  const scrubbed = scrubProps(props);
  const enriched = {
    ...scrubbed,
    app_name: scrubbed.app_name ?? currentConfig?.appName,
    timestamp: scrubbed.timestamp ?? new Date().toISOString(),
  };
  posthogInstance.capture(event, enriched);
}

/**
 * Identify a user. PII scrub runs on props. NEVER pass email/phone/SSN —
 * scrub catches accidental inclusion but the rule is: don't pass them.
 */
export function identifyPostHog(props: IdentifyProps): void {
  if (!isInitialized || !posthogInstance) return;
  const { userId, ...rest } = props;
  const scrubbed = scrubProps(rest);
  posthogInstance.identify(userId, scrubbed);
}

/**
 * Opt-out and clear cookies. Called by consent integration when user
 * revokes analytics consent.
 */
export function revokePostHog(): void {
  if (!isInitialized || !posthogInstance) return;
  posthogInstance.opt_out_capturing();
  posthogInstance.reset();
}

/**
 * Opt-in (after consent granted). Idempotent.
 */
export function grantPostHog(): void {
  if (!isInitialized || !posthogInstance) return;
  if (posthogInstance.has_opted_out_capturing()) {
    posthogInstance.opt_in_capturing();
  }
}

/** Test-only — reset module state. Not for runtime use. */
export function __resetForTests(): void {
  posthogInstance = null;
  isInitialized = false;
  currentConfig = null;
}
